import React from 'react';
import { Customer, CustomerCategory } from '../../types';
import { ZohoContact, ZohoPreviewSummary, ZohoSyncApprovalPlan } from '../../services/zohoCRM';

type ZohoApprovalAction = 'IMPORT' | 'UPDATE' | 'REVIEW' | 'SKIP';

interface ZohoImportModalProps {
  show: boolean;
  onClose: () => void;
  mode?: 'import' | 'link';
  linkTarget?: Customer | null;
  zohoContacts: ZohoContact[];
  isSearching: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  selectedCategoryId: string;
  setSelectedCategoryId: (value: string) => void;
  categories: CustomerCategory[];
  previewSummary?: ZohoPreviewSummary | null;
  onboardingContactId: string | null;
  isLoadingZoho?: boolean;
  customers: Customer[];
  onOnboardContact: (contact: ZohoContact) => void;
  onLinkContact?: (contact: ZohoContact) => void;
  onSyncAll?: (plan: ZohoSyncApprovalPlan) => void | Promise<void>;
}

const getDefaultApprovalAction = (contact: ZohoContact): ZohoApprovalAction => {
  const disposition = String(contact.syncDisposition || '').toUpperCase();
  if (disposition === 'LINKED') return 'UPDATE';
  if (disposition === 'DUPLICATE' || disposition === 'REVIEW') return 'REVIEW';
  return 'SKIP';
};

const getAllowedActions = (contact: ZohoContact): ZohoApprovalAction[] => {
  const disposition = String(contact.syncDisposition || '').toUpperCase();
  if (disposition === 'NEW') return ['IMPORT', 'SKIP'];
  if (disposition === 'LINKED') return ['UPDATE', 'SKIP'];
  if (disposition === 'DUPLICATE' || disposition === 'REVIEW') return ['REVIEW', 'SKIP'];
  return ['SKIP'];
};

const ZohoImportModal: React.FC<ZohoImportModalProps> = ({
  show,
  onClose,
  mode = 'import',
  linkTarget,
  zohoContacts,
  isSearching,
  searchQuery,
  setSearchQuery,
  selectedCategoryId,
  setSelectedCategoryId,
  categories,
  previewSummary,
  onboardingContactId,
  isLoadingZoho = false,
  customers,
  onOnboardContact,
  onLinkContact,
  onSyncAll,
}) => {
  const [approvalActions, setApprovalActions] = React.useState<Record<string, ZohoApprovalAction>>({});
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  React.useEffect(() => {
    if (!show || mode !== 'import') {
      setApprovalActions({});
      setShowConfirmModal(false);
      return;
    }
    setApprovalActions((prev) => {
      const next: Record<string, ZohoApprovalAction> = {};
      zohoContacts.forEach((contact) => {
        next[contact.contactId] = prev[contact.contactId] || getDefaultApprovalAction(contact);
      });
      return next;
    });
  }, [mode, show, zohoContacts]);

  if (!show) return null;
  const isLinkMode = mode === 'link';

  const approvalPlan = zohoContacts.reduce<ZohoSyncApprovalPlan>(
    (plan, contact) => {
      const action = approvalActions[contact.contactId] || getDefaultApprovalAction(contact);
      if (action === 'IMPORT') {
        plan.selectedNewContactIds = [...(plan.selectedNewContactIds || []), contact.contactId];
      } else if (action === 'UPDATE') {
        plan.selectedLinkedContactIds = [...(plan.selectedLinkedContactIds || []), contact.contactId];
      } else if (action === 'REVIEW') {
        plan.selectedReviewContactIds = [...(plan.selectedReviewContactIds || []), contact.contactId];
      } else {
        plan.skippedContactIds = [...(plan.skippedContactIds || []), contact.contactId];
      }
      return plan;
    },
    {}
  );

  const importCount = approvalPlan.selectedNewContactIds?.length || 0;
  const linkedCount = approvalPlan.selectedLinkedContactIds?.length || 0;
  const reviewCount = approvalPlan.selectedReviewContactIds?.length || 0;
  const skippedCount = approvalPlan.skippedContactIds?.length || 0;
  const hasApprovedChanges = importCount + linkedCount + reviewCount > 0;

  const updateApprovalAction = (contactId: string, action: ZohoApprovalAction) => {
    setApprovalActions((prev) => ({
      ...prev,
      [contactId]: action,
    }));
  };

  return (
    <>
      <div className="fixed inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-50 animate-in fade-in">
        <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-90 duration-300">
          <div className="p-10 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-black text-slate-800">
                {isLinkMode ? 'Link Zoho Contact' : 'Import from Zoho Books'}
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                {isLinkMode
                  ? `Select the matching Zoho contact for ${linkTarget?.name || 'this customer'}.`
                  : 'Preview Zoho contacts, approve imports, and safely refresh linked finance data.'}
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-10 overflow-y-auto flex-1">
            <div className="mb-6">
              <label className="text-sm font-black text-slate-500 uppercase tracking-wide mb-2 block">Search Contacts</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, company, or email (min 2 characters)..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 font-bold text-sm text-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none pr-12"
                />
                {isSearching && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  </div>
                )}
              </div>
              {!searchQuery && (
                <p className="text-xs text-slate-400 mt-2">Type at least 2 characters to search Zoho Books contacts</p>
              )}
            </div>

            {!isLinkMode && (
              <div className="mb-6">
                <label className="text-sm font-black text-slate-500 uppercase tracking-wide mb-2 block">
                  Assign to Discount Group (Optional)
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 font-bold text-sm text-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                >
                  <option value="">No Group (Manual Assignment Later)</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!!previewSummary && (
              <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
                {[
                  { label: 'New', value: previewSummary.newCount, tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                  { label: 'Linked', value: previewSummary.linkedCount, tone: 'bg-blue-50 text-blue-700 border-blue-200' },
                  { label: 'Duplicate', value: previewSummary.duplicateCount, tone: 'bg-amber-50 text-amber-700 border-amber-200' },
                  { label: 'Review', value: previewSummary.reviewCount, tone: 'bg-rose-50 text-rose-700 border-rose-200' },
                  { label: 'Skipped', value: previewSummary.skippedCount, tone: 'bg-slate-50 text-slate-600 border-slate-200' },
                ].map((item) => (
                  <div key={item.label} className={`rounded-2xl border px-4 py-3 ${item.tone}`}>
                    <p className="text-[10px] font-black uppercase tracking-widest">{item.label}</p>
                    <p className="mt-1 text-lg font-black">{item.value}</p>
                  </div>
                ))}
              </div>
            )}

            {!isLinkMode && zohoContacts.length > 0 && (
              <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-wrap gap-3 text-xs font-bold text-slate-600">
                  <span>Approved imports: {importCount}</span>
                  <span>Approved linked updates: {linkedCount}</span>
                  <span>Send to review: {reviewCount}</span>
                  <span>Skipped: {skippedCount}</span>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Safe default: new Zoho contacts stay skipped until explicitly approved for import.
                </p>
              </div>
            )}

            {isSearching ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="text-sm text-slate-400 mt-4">Searching Zoho Books contacts...</p>
                <p className="text-xs text-slate-300 mt-2">This may take a few seconds</p>
              </div>
            ) : zohoContacts.length === 0 ? (
              <div className="text-center py-20 text-slate-400">
                {searchQuery.length >= 2 ? (
                  <div>
                    <p className="text-sm font-bold mb-2">No contacts found matching "{searchQuery}"</p>
                    <p className="text-xs text-slate-400 mt-2">
                      Try a different search term or check if the contact exists in Zoho Books
                    </p>
                  </div>
                ) : (
                  <p className="text-sm font-bold">Start typing to search Zoho Books contacts</p>
                )}
              </div>
            ) : (
              <div className="space-y-3 max-h-[30rem] overflow-y-auto">
                {zohoContacts.map((contact) => {
                  const isOnboarding = onboardingContactId === contact.contactId;
                  const linkedCustomer = customers.find((c) => c.zohoContactId === contact.contactId);
                  const isLinkedToOther = !!linkedCustomer && (!linkTarget || linkedCustomer.id !== linkTarget.id);
                  const isLinkedToTarget = !!linkedCustomer && !!linkTarget && linkedCustomer.id === linkTarget.id;
                  const isAlreadyCustomer = customers.some(
                    (c) => c.zohoContactId === contact.contactId || (contact.email && c.email === contact.email)
                  );
                  const canLink = isLinkMode && !isLinkedToOther && !isLinkedToTarget;
                  const syncDisposition = String(contact.syncDisposition || '').toUpperCase();
                  const syncBadgeClass =
                    syncDisposition === 'NEW'
                      ? 'bg-emerald-100 text-emerald-700'
                      : syncDisposition === 'LINKED'
                        ? 'bg-blue-100 text-blue-700'
                        : syncDisposition === 'DUPLICATE'
                          ? 'bg-amber-100 text-amber-700'
                          : syncDisposition === 'REVIEW'
                            ? 'bg-rose-100 text-rose-700'
                            : 'bg-slate-100 text-slate-600';
                  const allowedActions = getAllowedActions(contact);
                  const currentAction = approvalActions[contact.contactId] || getDefaultApprovalAction(contact);

                  return (
                    <div
                      key={contact.id}
                      className={`bg-slate-50 rounded-xl p-4 border-2 ${
                        isLinkedToOther || isAlreadyCustomer ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200'
                      }`}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-black text-slate-800">{contact.contactName}</p>
                            {(isAlreadyCustomer || isLinkedToTarget) && (
                              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-bold">
                                {isLinkedToTarget ? 'Linked' : 'Already Added'}
                              </span>
                            )}
                            {isLinkedToOther && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-bold">
                                Linked to {linkedCustomer?.name}
                              </span>
                            )}
                            {contact.syncDisposition && (
                              <span className={`text-xs px-2 py-1 rounded-full font-bold ${syncBadgeClass}`}>
                                {contact.syncDisposition}
                              </span>
                            )}
                          </div>
                          {contact.companyName && contact.companyName !== contact.contactName && (
                            <p className="text-xs text-slate-500 mt-1">{contact.companyName}</p>
                          )}
                          {contact.email && <p className="text-xs text-slate-400 mt-1">{contact.email}</p>}
                          {contact.phone && <p className="text-xs text-slate-400 mt-1">Phone: {contact.phone}</p>}
                          {contact.syncReason && <p className="text-xs text-slate-500 mt-2">{contact.syncReason}</p>}
                          {Array.isArray(contact.matchedCustomers) && contact.matchedCustomers.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {contact.matchedCustomers.slice(0, 3).map((customer) => (
                                <span key={customer.id} className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
                                  {customer.name || customer.email || customer.id}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="w-full lg:w-64">
                          {contact.creditLimit && contact.creditLimit > 0 && (
                            <p className="text-xs text-slate-500 text-right">Credit: ${contact.creditLimit.toLocaleString()}</p>
                          )}
                          {contact.outstandingReceivable && contact.outstandingReceivable > 0 && (
                            <p className="text-xs text-rose-500 text-right">
                              Outstanding: ${contact.outstandingReceivable.toLocaleString()}
                            </p>
                          )}

                          {isLinkMode ? (
                            <button
                              onClick={() => onLinkContact?.(contact)}
                              disabled={isOnboarding || !canLink}
                              className={`mt-2 w-full px-4 py-2 text-xs font-black uppercase rounded-lg transition-all ${
                                isOnboarding || !canLink
                                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                                  : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg'
                              }`}
                            >
                              {isOnboarding ? 'Linking...' : isLinkedToTarget ? 'Linked' : 'Link'}
                            </button>
                          ) : (
                            <div className="mt-3 space-y-2">
                              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Approval Action
                              </label>
                              <select
                                value={currentAction}
                                onChange={(e) => updateApprovalAction(contact.contactId, e.target.value as ZohoApprovalAction)}
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700"
                              >
                                {allowedActions.map((action) => (
                                  <option key={action} value={action}>
                                    {action === 'IMPORT'
                                      ? 'Import new client'
                                      : action === 'UPDATE'
                                        ? 'Update linked client'
                                        : action === 'REVIEW'
                                          ? 'Send to review'
                                          : 'Skip'}
                                  </option>
                                ))}
                              </select>
                              {syncDisposition === 'NEW' && (
                                <button
                                  onClick={() => onOnboardContact(contact)}
                                  disabled={isOnboarding}
                                  className={`w-full px-4 py-2 text-xs font-black uppercase rounded-lg transition-all ${
                                    isOnboarding
                                      ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                                      : 'bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50'
                                  }`}
                                >
                                  {isOnboarding ? 'Adding...' : 'Import Only This Client'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-10 border-t border-slate-100 flex justify-end gap-4">
            {!isLinkMode && onSyncAll && (
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={isLoadingZoho || !hasApprovedChanges}
                className={`px-8 py-3 rounded-xl text-sm font-black uppercase transition-colors ${
                  isLoadingZoho || !hasApprovedChanges
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {isLoadingZoho ? 'Syncing...' : 'Review Pull Plan'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-8 py-3 text-sm font-black uppercase text-slate-400 hover:text-slate-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {!isLinkMode && showConfirmModal && onSyncAll && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70">
          <div className="w-full max-w-lg rounded-[2rem] bg-white p-8 shadow-2xl">
            <h4 className="text-xl font-black text-slate-900">Confirm Zoho Client Pull</h4>
            <p className="mt-3 text-sm text-slate-600">
              You are about to import {importCount} new clients and update {linkedCount} linked clients from Zoho. Continue?
            </p>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <p>Send to review: {reviewCount}</p>
              <p>Skipped: {skippedCount}</p>
              <p>Overwrite local fields: No</p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="rounded-xl px-5 py-3 text-sm font-black uppercase text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  void onSyncAll(approvalPlan);
                }}
                disabled={isLoadingZoho}
                className={`rounded-xl px-5 py-3 text-sm font-black uppercase ${
                  isLoadingZoho ? 'bg-slate-200 text-slate-400' : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {isLoadingZoho ? 'Syncing...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ZohoImportModal;
